import {
  type CreateMaintenanceProjectRequest,
  createMaintenanceProjectRequestSchema,
  type MaintenanceAddress,
  type MaintenanceProject,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { type Resolver, FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import {
  MaintenanceProjectSiteAddressFields,
  MaintenanceProjectStammdatenFields,
} from '@/products/maintenance/features/forms/project-form';
import { MaintenanceFormPage } from '@/products/maintenance/features/maintenance-form-page';
import {
  useCreateMaintenanceProject,
  useMaintenanceCustomer,
  useMaintenanceProject,
  useUpdateMaintenanceProject,
} from '@/products/maintenance/features/use-maintenance';
import {
  maintenanceCustomerPath,
  maintenanceProjectPath,
} from '@/products/maintenance/maintenance-paths';

function projectToFormValues(row: MaintenanceProject): CreateMaintenanceProjectRequest {
  return {
    customerId: row.customerId,
    name: row.name,
    projectNumber: row.projectNumber,
    description: row.description,
    siteAddress: {
      line1: row.siteAddress.line1,
      line2: row.siteAddress.line2,
      postalCode: row.siteAddress.postalCode,
      city: row.siteAddress.city,
      country: row.siteAddress.country,
    },
    isActive: row.isActive,
  };
}

function emptyProjectForm(
  customerId: string,
  customerAddress: MaintenanceAddress | null,
): CreateMaintenanceProjectRequest {
  const fromCustomer = customerAddress ?? {
    line1: '',
    line2: null,
    postalCode: '',
    city: '',
    country: 'DE',
  };
  return {
    customerId,
    name: '',
    projectNumber: null,
    description: null,
    siteAddress: { ...fromCustomer },
    isActive: true,
  };
}

export function MaintenanceProjectFormPage() {
  const { customerId: routeCustomerId = '', projectId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = location.pathname.endsWith('/bearbeiten');
  const isCreate = location.pathname.endsWith('/neu');

  usePageTitle(isEdit ? 'Projekt bearbeiten' : 'Projekt anlegen');

  const customer = useMaintenanceCustomer(isCreate ? routeCustomerId : '');
  const project = useMaintenanceProject(isEdit ? projectId : '');
  const create = useCreateMaintenanceProject();
  const update = useUpdateMaintenanceProject(projectId);
  const pending = create.isPending || update.isPending;

  const form = useForm<CreateMaintenanceProjectRequest>({
    resolver: zodResolver(
      createMaintenanceProjectRequestSchema,
    ) as Resolver<CreateMaintenanceProjectRequest>,
    defaultValues: emptyProjectForm(routeCustomerId, null),
  });

  useEffect(() => {
    if (isCreate && customer.data) {
      form.reset(emptyProjectForm(customer.data.id, customer.data.address));
    }
  }, [customer.data, form, isCreate]);

  useEffect(() => {
    if (isEdit && project.data) {
      form.reset(projectToFormValues(project.data));
    }
  }, [form, isEdit, project.data]);

  if (!isCreate && !isEdit) {
    return null;
  }

  if (isCreate && customer.isPending) {
    return <p className="text-sm text-muted-foreground">Kunde wird geladen …</p>;
  }
  if (isCreate && (customer.isError || !customer.data)) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Der Kunde wurde nicht gefunden.
      </p>
    );
  }
  if (isEdit && project.isPending) {
    return <p className="text-sm text-muted-foreground">Projekt wird geladen …</p>;
  }
  if (isEdit && (project.isError || !project.data)) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Das Projekt wurde nicht gefunden.
      </p>
    );
  }

  const cancelHref = isEdit
    ? maintenanceProjectPath(projectId)
    : maintenanceCustomerPath(routeCustomerId);

  async function onValid(values: CreateMaintenanceProjectRequest) {
    try {
      if (isEdit) {
        const saved = await update.mutateAsync({
          name: values.name,
          projectNumber: values.projectNumber,
          description: values.description,
          siteAddress: values.siteAddress,
          isActive: values.isActive,
        });
        toast.success('Projekt gespeichert.');
        void navigate(maintenanceProjectPath(saved.id));
        return;
      }
      const created = await create.mutateAsync(values);
      toast.success('Projekt angelegt.');
      void navigate(maintenanceProjectPath(created.id));
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : 'Das Projekt konnte nicht gespeichert werden.';
      toast.error(message);
    }
  }

  const customerName = isCreate ? customer.data?.name : project.data?.customer.name;

  return (
    <FormProvider {...form}>
      <MaintenanceFormPage
        title={isEdit ? 'Projekt bearbeiten' : 'Projekt anlegen'}
        description={customerName ? `Kunde: ${customerName}` : undefined}
        cancelHref={cancelHref}
        pending={pending}
        onSubmit={() => {
          void form.handleSubmit(onValid)();
        }}
      >
        <MaintenanceProjectStammdatenFields />
        <MaintenanceProjectSiteAddressFields />
      </MaintenanceFormPage>
    </FormProvider>
  );
}
